import React, { ComponentProps, PropsWithChildren } from 'react';
import clsx from 'clsx';
import { Tabs as AntTabs } from 'antd';

import { Colors, LineHeight } from '@common/tokens';
import { Tab } from './_tab';
import { Pane } from './_pane';

type AntdTabProps = ComponentProps<typeof AntTabs> & {
  containerStyle?: React.CSSProperties;
  stickyHeader?: boolean;
  headerBgTransparent?: boolean;
};

export const Tabs = ({
  children,
  containerStyle,
  headerBgTransparent,
  stickyHeader = false,
  ...props
}: PropsWithChildren<AntdTabProps>) => (
  <div
    className={clsx('wk-tabs', {
      'wk-tabs--sticky-header': stickyHeader,
      'wk-tabs--transparent-header': headerBgTransparent,
    })}
    style={containerStyle}
  >
    <AntTabs tabBarGutter={20} {...props}>
      {children}
    </AntTabs>
    <style jsx>{`
      .wk-tabs {
        height: 100%;
      }
      /*
       * Sticky tab bar: make the AntD root a full-height flex column so the nav
       * keeps its natural height and the content holder takes the rest and scrolls.
       */
      .wk-tabs--sticky-header :global(.ant-tabs) {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .wk-tabs--sticky-header :global(.ant-tabs-content-holder) {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
      }
      .wk-tabs :global(.ant-tabs-nav) {
        background-color: white;
      }
      .wk-tabs :global(.ant-tabs-tab) {
        color: ${Colors.elm};
        padding-bottom: 14px;
        transition: color 0.5s;
        text-align: center;
        font-weight: 900;
        text-transform: uppercase;
        font-style: normal;
        line-height: ${LineHeight.sm};
      }

      .wk-tabs :global(.ant-tabs-tab:hover),
      .wk-tabs :global(.ant-tabs-tab-active) {
        color: ${Colors.elmDarker};
      }

      .wk-tabs :global(.ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn) {
        text-shadow: none;
      }

      .wk-tabs :global(.ant-tabs-ink-bar) {
        height: 4px;
      }

      .wk-tabs :global(.ant-tabs-nav) {
        border-bottom: none;
        box-shadow: inset 0 -4px 0 rgba(223, 239, 242);
        margin-bottom: 0;
      }
      /* Overrides the default white nav background above — declared last to win. */
      .wk-tabs--transparent-header :global(.ant-tabs-nav) {
        background-color: transparent;
      }
      {/* fix: redundant gutter */}
      .wk-tabs :global(.ant-tabs-tab + .ant-tabs-tab) {
        margin: 0;
      }
    `}</style>
  </div>
);

Tabs.Pane = Pane;
Tabs.Tab = Tab;
